import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SharedDataProvider } from './context/SharedDataContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './i18n';
import './App.css';
import { setOnUpdateAvailable, applyUpdate } from './serviceWorkerRegistration';

import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';
import Layout from './components/Layout';
import MobileLayout from './components/MobileLayout';

// Critical pages - load immediately
import Login from './pages/Login';
import Pending from './pages/Pending';
import Onboarding from './pages/Onboarding';

// Lazy load ALL other pages for better performance
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const RegisterCompany = lazy(() => import('./pages/RegisterCompany'));
const JoinCompany = lazy(() => import('./pages/JoinCompany'));
const JoinRequests = lazy(() => import('./pages/JoinRequests'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Employees = lazy(() => import('./pages/Employees'));
const Departments = lazy(() => import('./pages/Departments'));
const Attendance = lazy(() => import('./pages/Attendance'));
const Leaves = lazy(() => import('./pages/Leaves'));
const Salaries = lazy(() => import('./pages/Salaries'));
const Reports = lazy(() => import('./pages/Reports'));
const MySalary = lazy(() => import('./pages/MySalary'));
const Branches = lazy(() => import('./pages/Branches'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Settings = lazy(() => import('./pages/Settings'));
const Bonuses = lazy(() => import('./pages/Bonuses'));
const SalaryReports = lazy(() => import('./pages/SalaryReports'));
const SuspendedSalaries = lazy(() => import('./pages/SuspendedSalaries'));
const BroadcastNotification = lazy(() => import('./pages/BroadcastNotification'));
const NotificationTest = lazy(() => import('./pages/NotificationTest'));
const Duties = lazy(() => import('./pages/Duties'));
const Terms = lazy(() => import('./pages/Terms'));
const Vehicles = lazy(() => import('./pages/Vehicles'));
const KPI = lazy(() => import('./pages/KPI'));
const Accounts = lazy(() => import('./pages/Accounts'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Permissions = lazy(() => import('./pages/Permissions'));
const Penalties = lazy(() => import('./pages/Penalties'));
const Tasks = lazy(() => import('./pages/Tasks'));
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'));
const CompleteProfile = lazy(() => import('./pages/CompleteProfile'));
const EmployeeCodeLogin = lazy(() => import('./pages/EmployeeCodeLogin'));
const More = lazy(() => import('./pages/More'));
const ColorDemo = lazy(() => import('./pages/ColorDemo'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));

// Finance Module Pages
const Budgets = lazy(() => import('./pages/Budgets'));
const FundingRequests = lazy(() => import('./pages/FundingRequests'));
const CashFlow = lazy(() => import('./pages/CashFlow'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#0F2A1D]"></div>
  </div>
);

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes - Landing & Registration */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/home" element={<LandingPage />} />
        <Route path="/register-company" element={<RegisterCompany />} />
        <Route path="/join" element={<JoinCompany />} />
        
        {/* Public Routes - Auth */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/pending" element={<Pending />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/welcome" element={<Onboarding />} />
        <Route path="/color-demo" element={<ColorDemo />} />
        <Route path="/complete-profile" element={<CompleteProfile />} />
        <Route path="/employee-code-login" element={<EmployeeCodeLogin />} />

        {/* Protected Routes - Mobile Layout (بدون header) */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MobileLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="attendance" element={<Attendance />} />
          <Route path="leaves" element={<Leaves />} />
          <Route path="salary" element={<MySalary />} />
          <Route path="my-salary" element={<MySalary />} />
          <Route path="more" element={<More />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="profile" element={<Settings />} />
          <Route path="settings" element={<Settings />} />
          <Route path="performance" element={<KPI />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="penalties" element={<Penalties />} />
          <Route path="bonuses" element={<Bonuses />} />
          <Route path="complete-profile" element={<CompleteProfile />} />
          
          {/* Admin Routes - Still use Mobile Layout */}
          <Route path="employees" element={<Employees />} />
          <Route path="departments" element={<Departments />} />
          <Route path="branches" element={<Branches />} />
          <Route path="join-requests" element={<JoinRequests />} />
          <Route path="salaries" element={<Salaries />} />
          <Route path="reports" element={<Reports />} />
          <Route path="duties" element={<Duties />} />
          <Route path="vehicles" element={<Vehicles />} />
          <Route path="kpi" element={<KPI />} />
          <Route path="accounts" element={<Accounts />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="permissions" element={<Permissions />} />
          <Route path="knowledge-base" element={<KnowledgeBase />} />
          <Route path="salary-reports" element={<SalaryReports />} />
          <Route path="suspended-salaries" element={<SuspendedSalaries />} />
          <Route path="subscription" element={<SubscriptionPage />} />
          <Route path="payment-success" element={<PaymentSuccess />} />
          <Route path="broadcast" element={<BroadcastNotification />} />
          <Route path="notification-test" element={<NotificationTest />} />
          <Route path="terms" element={<Terms />} />
          <Route path="subscription" element={<SubscriptionPage />} />
          
          {/* Finance Module Routes */}
          <Route path="budgets" element={<Budgets />} />
          <Route path="funding-requests" element={<FundingRequests />} />
          <Route path="cash-flow" element={<CashFlow />} />
          
          <Route path="" element={<Navigate to="/dashboard" replace />} />
        </Route>
    </Routes>
    </Suspense>
  );
}

// Temporary Coming Soon Component
const ComingSoon = ({ page }) => {
  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <div className="text-6xl mb-4">🚧</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">{page} Page</h2>
        <p className="text-gray-600">Coming Soon...</p>
      </div>
    </div>
  );
};

// Update Banner Component
const UpdateBanner = ({ onUpdate }) => {
  const lang = localStorage.getItem('language') || 'ar';
  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-4 py-3 shadow-lg animate-slideDown">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🎉</span>
          <div>
            <p className="font-bold text-sm sm:text-base">
              {lang === 'ar' ? 'تحديث جديد متاح!' : 'New update available!'}
            </p>
            <p className="text-xs sm:text-sm text-emerald-100">
              {lang === 'ar' ? 'اضغط للتحديث والحصول على أحدث الميزات' : 'Click to update and get the latest features'}
            </p>
          </div>
        </div>
        <button
          onClick={onUpdate}
          className="px-4 py-2 bg-white text-emerald-600 rounded-lg font-bold text-sm hover:bg-emerald-50 transition-all shadow-md"
        >
          {lang === 'ar' ? 'تحديث الآن' : 'Update Now'}
        </button>
      </div>
    </div>
  );
};

function App() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  
  useEffect(() => {
    // Set initial language direction - Default to Arabic
    const lang = localStorage.getItem('language') || 'ar';
    document.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    
    // Register update callback
    setOnUpdateAvailable(() => {
      setUpdateAvailable(true);
    });
  }, []);
  
  const handleUpdate = () => {
    setUpdateAvailable(false);
    applyUpdate();
  };

  return (
    <AuthProvider>
      <SharedDataProvider>
        <BrowserRouter>
          {updateAvailable && <UpdateBanner onUpdate={handleUpdate} />}
          <AppRouter />
          <ToastContainer position="top-right" autoClose={3000} />
        </BrowserRouter>
      </SharedDataProvider>
    </AuthProvider>
  );
}

export default App;